import { t, Trans } from '@lingui/macro'
import { Box, Typography } from '@material-ui/core'
import { Field, Form, Formik } from 'formik'
import React, { useCallback } from 'react'
import styled from 'react-emotion/macro'
import { EBillingPlan } from 'shopify-payment-core'
import * as Yup from 'yup'

import { FormTextField } from '../atoms/FormTextField'
import { SubmitButton } from '../atoms/SubmitButton'
import { appConfig } from '../config'
import { useFirebaseFunctions } from '../hooks/useFirebaseFunctions'
import { useSlack } from '../hooks/useSlack'
import { useSnackBar } from '../hooks/useSnackBar'
import { i18n } from '../i18n'
import { WizardStep } from '../typings/types'

const BigNumber = styled.span`
  font-size: 1.2rem;
  font-weight: bold;
`

interface IFormValues {
  identity: string
  plan: EBillingPlan
}

const formSchema = Yup.object().shape({
  identity: Yup.string()
    .required()
    .min(5),
  plan: Yup.string().oneOf([EBillingPlan.MONTHLY, EBillingPlan.ONETIME]),
})

export const BillingStep: WizardStep = ({ account }) => {
  const { trialRemaining } = account
  const { setBilling } = useFirebaseFunctions()
  const { enqueueSuccess, enqueueError } = useSnackBar()
  const { sendMessage } = useSlack()

  const initialValues: IFormValues = {
    identity: account.billingIdentity || '',
    plan: account.billingPlan || EBillingPlan.FREE,
  }

  const onSubmit = useCallback(async (values: IFormValues) => {
    const result = await setBilling({
      accountId: account.id,
      segmentKey: account.segmentKey,
      identity: values.identity,
      plan: values.plan,
    })

    const slackMessageDetail = `\n*Plán:* ${values.plan}\n*IČ:* ${values.identity}\n\n${appConfig.uri}/${account.id}`

    if (!result.data.success) {
      enqueueError(
        i18n.t`Fakturaci se nepodařilo nastavit. Prosím kontaktujte nás.`,
      )
      sendMessage(
        `Fakturaci pro obchod *${account.shopName}* se nepodařilo nastavit.${slackMessageDetail}`,
      )
    } else {
      enqueueSuccess(
        i18n.t`Fakturace úspěšně nastavena. Brzy Vás budeme kontaktovat.`,
      )
      sendMessage(
        `Fakturace potvrzena pro obchod *${account.shopName}*${slackMessageDetail}`,
      )
    }
  }, [])

  const trialStatus =
    trialRemaining > 0 ? (
      <>
        <Typography>
          <Trans>
            Aktuálně můžete provést ještě{' '}
            <BigNumber>{trialRemaining}</BigNumber> platby zdarma.
          </Trans>
        </Typography>
        <br />
        <Typography>
          <Trans>
            Po vyčerpání všech plateb zdarma bude platební brána blokována. Od
            vystavení faktury a její proplacení z Vaší strany může uběhnout až
            několik dní. Pokud tedy chcete zajistit co nejkratší dobu
            nefunkčnosti, doporučujeme vyplnit následující formulář co nejdříve.
          </Trans>
        </Typography>
      </>
    ) : (
      <Typography variant="h6">
        <Trans>
          Vyčerpali jste všechny platby zdarma. Funkčnost integrace je
          pozastavena dokud nedojde k obdržení platby za službu.
        </Trans>
      </Typography>
    )

  return (
    <>
      {trialStatus}
      <br />
      <Typography>
        <Trans>
          Aktuální cena integrace je 250Kč / měsíc. V blízké budoucnosti
          plánujeme nový způsob oceněnení. Budeme Vás včas informovat o těchto
          změnách.
        </Trans>
      </Typography>
      <br />
      <Formik<IFormValues>
        onSubmit={onSubmit}
        validationSchema={formSchema}
        isInitialValid={formSchema.isValidSync(initialValues)}
        initialValues={initialValues}
      >
        <Form>
          <Field
            name="identity"
            component={FormTextField}
            label={i18n._(t`Identifikační číslo (IČ)`)}
          />
          <Box height="1rem" />
          <SubmitButton>
            <Trans>Potvrdit</Trans>
          </SubmitButton>
        </Form>
      </Formik>
    </>
  )
}

BillingStep.getStepLabel = () => <Trans>Platba za integraci</Trans>
